import { useState } from 'react';
import { api } from '../api';
import type { Capability, CapabilityGroup } from '../api';
import { useResource } from '../hooks';
import { StatusBadge } from '../components/ui';
import { PageError, PageLoading, Section } from '../components/primitives';
import { ResponsiveTable } from '../components/primitives/ResponsiveTable';
import type { Column } from '../components/primitives/ResponsiveTable';

const groupLabel: Record<CapabilityGroup, string> = {
  projects: 'Projects and access',
  servers: 'Servers',
  deployments: 'Deployments and sites',
  operations: 'Operations',
};

const groupOrder: CapabilityGroup[] = ['projects', 'servers', 'deployments', 'operations'];

/**
 * Settings ▸ Platform (CO-160): what this installation can do, by purpose.
 * Each capability is its name, one sentence, whether it is available and the
 * one limitation worth knowing; the technical description and the documents
 * behind it open on request, so no internal path is part of the list.
 */
export function CapabilitiesPage() {
  const capabilities = useResource(() => api.capabilities(), [], undefined, { key: 'capabilities' });
  const [open, setOpen] = useState<string | null>(null);

  // Inside the Settings tab, whose header stays; a failed read can be tried again (CO-184, OR3).
  if (capabilities.loading && !capabilities.data) return <PageLoading rows={8} />;
  if (capabilities.error) return <PageError error={capabilities.error} onRetry={capabilities.reload} />;
  const data = capabilities.data;
  if (!data) return null;

  const columns: Column<Capability>[] = [
    {
      id: 'capability',
      header: 'Capability',
      card: 'title',
      width: 'fill',
      cell: (capability) => (
        <span className="cell-stack">
          <span className="cell-stack__primary">{capability.name}</span>
          <span className="cell-stack__secondary capability__summary">{capability.summary || capability.detail}</span>
        </span>
      ),
    },
    {
      id: 'availability',
      header: 'Availability',
      card: 'status',
      width: 'status',
      cell: (capability) => <StatusBadge kind="implementation" value={capability.status} />,
    },
    {
      id: 'limitation',
      header: 'Limitation',
      width: 28,
      cell: (capability) => (capability.limitation ? <span>{capability.limitation}</span> : <span className="muted">None</span>),
    },
  ];

  return (
    <div className="capabilities" data-testid="capabilities">
      {groupOrder.map((group) => {
        const items = data.capabilities.filter((capability) => (capability.group ?? 'projects') === group);
        if (items.length === 0) return null;
        return (
          <Section key={group} title={groupLabel[group]} testId={`capabilities-${group}`}>
            <ResponsiveTable
              rows={items}
              rowKey={(capability) => capability.key}
              caption={groupLabel[group]}
              className="capabilities-table"
              columns={columns}
              actionsSize="md"
              actions={(capability) => (
                <button
                  type="button"
                  className="button button--ghost button--small"
                  aria-expanded={open === capability.key}
                  aria-controls={open === capability.key ? `capability-detail-${capability.key}` : undefined}
                  aria-label={`${open === capability.key ? 'Hide' : 'Show'} technical details of ${capability.name}`}
                  onClick={() => setOpen((current) => (current === capability.key ? null : capability.key))}
                >
                  {open === capability.key ? 'Hide details' : 'Details'}
                </button>
              )}
              detail={(capability) =>
                open === capability.key ? (
                  <div className="capability__detail" id={`capability-detail-${capability.key}`} data-testid={`capability-detail-${capability.key}`}>
                    <p>{capability.detail}</p>
                    {capability.references && capability.references.length > 0 ? (
                      <p className="field__help">
                        Documents:{' '}
                        {capability.references.map((reference, index) => (
                          <span key={reference}>
                            {index > 0 ? ', ' : ''}
                            <code translate="no">{reference}</code>
                          </span>
                        ))}
                      </p>
                    ) : null}
                  </div>
                ) : null
              }
              rowProps={(capability) => ({ testId: `capability-${capability.key}` })}
            />
          </Section>
        );
      })}
    </div>
  );
}
